import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Activity, TrendingUp, TrendingDown, Target, ShieldAlert,
  ShoppingCart, ArrowRight, Zap, BarChart3, Gauge, Eye,
  CircleDot, Radio, RefreshCw, Clock, AlertTriangle,
  CheckCircle, XCircle, Minus
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Signal() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pulse, setPulse] = useState(false);

  const fetchSignal = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/signal/live`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setPulse(true);
        setTimeout(() => setPulse(false), 500);
      }
    } catch (err) {
      console.error('Signal fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSignal();
    const interval = setInterval(fetchSignal, 10000);
    return () => clearInterval(interval);
  }, [fetchSignal]);

  const handleBuy = async () => {
    if (!data?.best_stock?._id) return;
    try {
      const res = await fetch(`${API}/api/recommendations/${data.best_stock._id}/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: 1 }),
      });
      if (res.ok) {
        toast.success(`Bought ${data.best_stock.symbol} at $${data.best_stock.buy_price}`);
        fetchSignal();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to buy');
      }
    } catch {
      toast.error('Network error');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-80 w-full rounded-xl bg-[hsl(var(--surface-2))]" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl bg-[hsl(var(--surface-2))]" />)}
        </div>
        <Skeleton className="h-48 w-full rounded-xl bg-[hsl(var(--surface-2))]" />
      </div>
    );
  }

  const signal = data?.signal || 'NO_SIGNAL';
  const signalColor = data?.signal_color || 'red';
  const probability = data?.success_probability || 0;
  const stock = data?.best_stock;
  const factors = data?.factors || {};

  const getSignalGlow = () => {
    if (signalColor === 'green') return 'shadow-[0_0_80px_-10px_rgba(46,196,142,0.4)]';
    if (signalColor === 'yellow') return 'shadow-[0_0_80px_-10px_rgba(214,175,55,0.3)]';
    return 'shadow-[0_0_80px_-10px_rgba(220,80,80,0.3)]';
  };

  const getSignalBorderColor = () => {
    if (signalColor === 'green') return 'border-emerald-500/40';
    if (signalColor === 'yellow') return 'border-[hsl(var(--primary))]/40';
    return 'border-red-500/40';
  };

  const getSignalTextColor = () => {
    if (signalColor === 'green') return 'text-emerald-400';
    if (signalColor === 'yellow') return 'text-[hsl(var(--primary))]';
    return 'text-red-400';
  };

  const getSignalBgColor = () => {
    if (signalColor === 'green') return 'bg-emerald-500/10';
    if (signalColor === 'yellow') return 'bg-[hsl(var(--primary))]/10';
    return 'bg-red-500/10';
  };

  const getProbabilityColor = () => {
    if (probability >= 70) return 'bg-emerald-500';
    if (probability >= 50) return 'bg-[hsl(var(--primary))]';
    if (probability >= 30) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="signal-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Radio className={`w-6 h-6 ${getSignalTextColor()} ${signal === 'BUY' ? 'animate-pulse' : ''}`} />
            Live Signal
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">
            Real-time AI analysis of the best stock opportunity right now
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-xs ${
            data?.market_open ? 'text-emerald-400 border-emerald-500/30' : 'text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]'
          }`}>
            <span className={`w-2 h-2 rounded-full mr-1.5 inline-block ${data?.market_open ? 'bg-emerald-400 animate-pulse' : 'bg-[hsl(var(--muted-foreground))]'}`} />
            Market {data?.market_open ? 'Open' : 'Closed'}
          </Badge>
          {data?.is_analyzing && (
            <Badge variant="outline" className="text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30 text-xs">
              <Activity className="w-3 h-3 mr-1 animate-pulse" /> Analyzing
            </Badge>
          )}
        </div>
      </div>

      {/* MAIN SIGNAL CARD */}
      <Card className={`relative overflow-hidden bg-[hsl(var(--card))] border-2 ${getSignalBorderColor()} rounded-2xl ${getSignalGlow()} transition-shadow duration-500`}>
        <CardContent className="p-6 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left - Signal Indicator */}
            <div className="flex flex-col items-center justify-center text-center">
              {/* Big Signal Circle */}
              <div className={`relative w-40 h-40 md:w-52 md:h-52 rounded-full ${getSignalBgColor()} flex items-center justify-center mb-6 ${pulse ? 'scale-105' : 'scale-100'} transition-transform duration-300`}>
                <div className={`absolute inset-2 rounded-full border-4 ${getSignalBorderColor()} flex items-center justify-center`}>
                  <div className="text-center">
                    {signalColor === 'green' ? (
                      <CheckCircle className="w-12 h-12 md:w-16 md:h-16 text-emerald-400 mx-auto mb-1" />
                    ) : signalColor === 'yellow' ? (
                      <Eye className="w-12 h-12 md:w-16 md:h-16 text-[hsl(var(--primary))] mx-auto mb-1" />
                    ) : (
                      <XCircle className="w-12 h-12 md:w-16 md:h-16 text-red-400 mx-auto mb-1" />
                    )}
                    <span className={`text-sm md:text-base font-bold ${getSignalTextColor()}`}>
                      {signal === 'BUY' ? 'BUY NOW' : signal === 'WATCH' ? 'WATCH' : signal === 'WAIT' ? 'WAIT' : 'NO SIGNAL'}
                    </span>
                  </div>
                </div>
                {/* Animated ring for BUY signal */}
                {signalColor === 'green' && (
                  <div className="absolute inset-0 rounded-full border-2 border-emerald-400/20 animate-ping" />
                )}
              </div>

              <h2 className={`text-xl md:text-2xl font-bold ${getSignalTextColor()} mb-1`}>
                {data?.signal_label || 'Waiting for Signal'}
              </h2>
              <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-sm">
                {data?.signal_description}
              </p>

              {/* Last Updated */}
              <div className="flex items-center gap-1 mt-3 text-xs text-[hsl(var(--muted-foreground))]">
                <Clock className="w-3 h-3" />
                Updated: {data?.last_updated ? new Date(data.last_updated).toLocaleTimeString() : '--'}
              </div>
            </div>

            {/* Right - Success Probability + Stock Info */}
            <div className="space-y-6">
              {/* Success Probability */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-[hsl(var(--primary))]" />
                    <span className="text-sm font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Success Probability</span>
                  </div>
                  <span className={`font-mono text-3xl md:text-4xl font-black tabular-nums ${getSignalTextColor()}`}>
                    {probability}%
                  </span>
                </div>
                <div className="h-4 rounded-full bg-[hsl(var(--surface-2))] overflow-hidden">
                  <div
                    className={`h-full rounded-full ${getProbabilityColor()} confidence-fill`}
                    style={{ width: `${probability}%` }}
                  />
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
                  Based on AI analysis, market conditions, risk/reward, and momentum
                </p>
              </div>

              {/* Stock Details */}
              {stock && (
                <>
                  <Separator className="bg-[hsl(var(--border))]" />
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl md:text-2xl font-bold font-mono">{stock.symbol}</h3>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{stock.company_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-xl md:text-2xl font-bold tabular-nums">
                          ${data?.live_price?.toFixed(2) || stock.buy_price?.toFixed(2)}
                        </p>
                        {data?.price_vs_buy_pct !== undefined && (
                          <span className={`font-mono text-xs tabular-nums flex items-center justify-end gap-0.5 ${
                            data.price_vs_buy_pct <= 0 ? 'text-emerald-400' : data.price_vs_buy_pct < 2 ? 'text-[hsl(var(--primary))]' : 'text-red-400'
                          }`}>
                            {data.price_vs_buy_pct <= 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                            {data.price_vs_buy_pct >= 0 ? '+' : ''}{data.price_vs_buy_pct.toFixed(2)}% vs buy
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Price Levels */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="p-3 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
                        <div className="flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))] mb-1">
                          <ShoppingCart className="w-3 h-3" /> Buy
                        </div>
                        <p className="font-mono text-sm font-semibold tabular-nums">${stock.buy_price?.toFixed(2)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
                        <div className="flex items-center gap-1 text-[10px] text-emerald-400 mb-1">
                          <Target className="w-3 h-3" /> Target
                        </div>
                        <p className="font-mono text-sm font-semibold tabular-nums text-emerald-400">${stock.target_price?.toFixed(2)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
                        <div className="flex items-center gap-1 text-[10px] text-red-400 mb-1">
                          <ShieldAlert className="w-3 h-3" /> Stop
                        </div>
                        <p className="font-mono text-sm font-semibold tabular-nums text-red-400">${stock.stop_loss?.toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      {stock.status === 'active' && signalColor === 'green' && (
                        <Button
                          onClick={handleBuy}
                          className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-semibold text-base py-5"
                          data-testid="signal-buy-button"
                        >
                          <ShoppingCart className="w-5 h-5 mr-2" />
                          Buy Now - ${stock.buy_price?.toFixed(2)}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        className="flex-1 border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                        onClick={() => navigate(`/recommendation/${stock._id}`)}
                        data-testid="signal-view-analysis"
                      >
                        View Full Analysis <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Probability Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <FactorCard label="AI Confidence" value={factors.confidence} max={35} icon={Zap} />
        <FactorCard label="Risk/Reward" value={factors.risk_reward} max={20} icon={BarChart3} />
        <FactorCard label="Entry Quality" value={factors.entry_quality} max={15} icon={Target} />
        <FactorCard label="Market" value={factors.market} max={15} icon={TrendingUp} />
        <FactorCard label="Momentum" value={factors.momentum} max={10} icon={Activity} />
        <FactorCard label="Track Record" value={factors.historical} max={5} icon={CheckCircle} />
      </div>

      {/* Market Overview + Extra Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Market Condition */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-4">
              Market Conditions
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[hsl(var(--muted-foreground))]">Market Condition</span>
                <Badge variant="outline" className={`${
                  data?.market_condition?.includes('BULLISH') ? 'text-emerald-400 border-emerald-500/30' :
                  data?.market_condition?.includes('BEARISH') ? 'text-red-400 border-red-500/30' :
                  'text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30'
                }`}>
                  {data?.market_condition || 'N/A'}
                </Badge>
              </div>
              {data?.risk_reward_ratio > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">Risk/Reward Ratio</span>
                  <span className="font-mono text-sm font-semibold tabular-nums">{data.risk_reward_ratio}:1</span>
                </div>
              )}
              {data?.momentum !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">Momentum</span>
                  <span className={`font-mono text-sm font-semibold tabular-nums flex items-center gap-1 ${
                    data.momentum > 0 ? 'text-emerald-400' : data.momentum < 0 ? 'text-red-400' : 'text-[hsl(var(--muted-foreground))]'
                  }`}>
                    {data.momentum > 0 ? <TrendingUp className="w-3 h-3" /> : data.momentum < 0 ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                    {data.momentum > 0 ? '+' : ''}{data.momentum}%
                  </span>
                </div>
              )}
              {data?.volume_ratio && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">Volume</span>
                  <span className={`font-mono text-sm font-semibold tabular-nums ${data.volume_ratio > 1.2 ? 'text-emerald-400' : 'text-[hsl(var(--muted-foreground))]'}`}>
                    {data.volume_ratio}x avg
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Trade Plan */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-4">
              Trade Plan
            </h3>
            {stock ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">Distance to Target</span>
                  <span className="font-mono text-sm font-semibold tabular-nums text-emerald-400">
                    +{data?.distance_to_target_pct?.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">Distance to Stop</span>
                  <span className="font-mono text-sm font-semibold tabular-nums text-red-400">
                    -{data?.distance_to_stop_pct?.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">Est. Return</span>
                  <span className="font-mono text-sm font-semibold tabular-nums text-emerald-400">
                    +{stock.estimated_return_pct?.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">Timeframe</span>
                  <span className="text-sm font-medium">{stock.timeframe || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">Win Rate (Historical)</span>
                  <span className="font-mono text-sm font-semibold tabular-nums">{data?.historical_win_rate?.toFixed(0)}%</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">No active trade plan. Run analysis to generate signals.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FactorCard({ label, value = 0, max = 100, icon: Icon }) {
  const pct = max > 0 ? (value / max * 100) : 0;
  const getColor = () => {
    if (pct >= 70) return 'text-emerald-400';
    if (pct >= 40) return 'text-[hsl(var(--primary))]';
    return 'text-red-400';
  };
  const getBarColor = () => {
    if (pct >= 70) return 'bg-emerald-500';
    if (pct >= 40) return 'bg-[hsl(var(--primary))]';
    return 'bg-red-500';
  };

  return (
    <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Icon className={`w-3.5 h-3.5 ${getColor()}`} />
          <span className="text-[10px] text-[hsl(var(--muted-foreground))] font-medium uppercase tracking-wider truncate">{label}</span>
        </div>
        <div className="flex items-end justify-between mb-1.5">
          <span className={`font-mono text-lg font-bold tabular-nums ${getColor()}`}>{value?.toFixed(0)}</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">/{max}</span>
        </div>
        <div className="h-1.5 rounded-full bg-[hsl(var(--surface-2))] overflow-hidden">
          <div className={`h-full rounded-full ${getBarColor()} confidence-fill`} style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
      </CardContent>
    </Card>
  );
}
